import { api } from '@/global/services/api'
import { isObject } from 'lodash'
import { formatIsoDateCustom, validateDateTimeFormat, currentDatetime, checkDateFormat } from '@/global/services/helpers/dates'
import store from '@/global/store'
import documentsHistoryHelper from '../documents-history-helper'

const FORM_DATE_FORMAT = 'DD.MM.YYYY.'
const DATE_FORMAT_FROM_REQUEST = 'YYYY-MM-DD HH:mm:ss'
const DATE_FORMAT_TO_SEND = 'YYYY-MM-DD'
const FORM_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'
const DISPLAY_DATETIME_FORMAT = 'DD.MM.YYYY. HH:mm'
const DOCUMENTS_HISTORY_DATA_KEY = 'approvalDocuments'
const DOCUMENT_TYPE = 'consent'
export default {

  mixins: [
    documentsHistoryHelper
  ],

  data () {
    return {
      approvalHistoryVisible: false,
      approvalShowEditButtons: false,
      approvalEditState: false,
      approvalShowDocumentPreview: false,
      approvalDocument: null,
      approvalDataForGenerationVisible: true,
      approvalTitle: this.$t('road-maintenance/aggregator.approval'),
      approvalBaseTitle: this.$t('road-maintenance/aggregator.approval'),
      approvalValidationErrors: {},
      approvalShowLoader: false,
      approvalUploadAttachmentEvent: null,
      approvalDocumentsVisible: true,
      approvalAttachmentsColumnsConfig: [],
      approvalDocumentHistoryLoaded: false,
      approvalRefreshShowLoader: false,
      approvalRefreshDisabled: false
    }
  },

  computed: {
    approvalConfig () {
      return {
        title: this.$t('road-maintenance/aggregator.approval'),
        visible: this.$route.query.category !== 'request_new',
        key: 'approval',
        validationErrors: this.approvalValidationErrors,
        apiConfig: {
          post: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}/store`,
            usePatch: false,
            method: 'post'
          },
          get: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}`,
            method: 'get'
          }
        },
        edit: this.approvalEditState,
        editUpdate: (event) => this.onApprovalEditUpdate(event),
        dataFetched: (event) => this.onApprovalDataFetched(event),
        afterCancel: (event) => this.onApprovalDataFetched(event),
        afterSave: (event) => this.onApprovalDataFetched(event, true),
        modifyDataBeforeSend: (event) => this.onApprovalModifyData(event),
        hiddenFormKeys: [
          'erp_reference_number',
          'additional_terms',
          'payment_type',
          'additional_terms_note',
          'html',
          'documents',
          'files'
        ],
        tabContent: [
          {
            component: this.tabActionsComponent,
            title: this.approvalTitle,
            showBackButton: {
              uiViewName: this.$route?.meta?.parent_vue_name ?? null
            },
            showEditButtons: this.approvalShowEditButtons,
            forceAdditionalButtonsDisplay: true,
            additional_buttons: [
              {
                type: 'base',
                appendIcon: 'mdi-refresh',
                visible: !this.approvalShowEditButtons,
                showLoader: this.approvalRefreshShowLoader,
                disabled: this.approvalRefreshDisabled,
                click: async () => {
                  this.approvalRefreshShowLoader = true
                  this.approvalRefreshDisabled = true

                  await this.fetchHistoryDocuments(DOCUMENT_TYPE, DOCUMENTS_HISTORY_DATA_KEY)

                  this.approvalRefreshShowLoader = false
                  setTimeout(() => {
                    this.approvalRefreshDisabled = false
                  }, this.refreshTabBtnTimeout)
                }
              },
              {
                type: 'base',
                label: this.$t('fleet/documents/prl_cargo.change_status'),
                prependIcon: 'mdi-swap-horizontal',
                visible: !this.approvalShowEditButtons,
                click: (event) => this.onChangeStatus(event)
              },
              {
                type: 'dropdown',
                buttonType: 'base',
                label: this.$t('road-maintenance/aggregator.generate_document'),
                color: '#65AD45',
                class: 'generate-document-btn',
                visible: !this.approvalShowEditButtons,
                menuContentClass: 'menu-class',
                component: this.documentsGenerationDropdownMenuComponent,
                hasHistoryData: this.logsData?.[DOCUMENTS_HISTORY_DATA_KEY]?.length,
                menuItemClick: (event) => this.approvalMenuItemClick(event)
              },
              {
                type: 'base',
                label: this.$t('fleet/documents/prl_cargo.view_pdf'),
                color: '#6e736d',
                visible: this.approvalShowEditButtons,
                class: 'generate-document-btn',
                click: (event) => this.previewDocument(event, DOCUMENT_TYPE, this.approvalBaseTitle)
              }
            ],
            onCancelTabSave: () => this.onApprovalCancelTab()
          },
          // Data for document generation
          {
            component: this.columnsContentComponent,
            title: this.$t('road-maintenance/aggregator.data_for_approval_generation'),
            visible: this.approvalDataForGenerationVisible,
            rows: [
              {
                type: 'fields',
                columnsCount: 4,
                fields: [
                  {
                    key: 'doc_class',
                    label: this.$t('road-maintenance/aggregator.class'),
                    row: 1,
                    column: 1,
                    colSpan: 1.5,
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'registry_number',
                    row: 1,
                    column: 2,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/excavation-service/tabs/approval/columns.registration_number'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'created_at',
                    type: 'datetime-picker',
                    subType: 'date',
                    row: 1,
                    column: 3,
                    colSpan: 1.5,
                    appendIcon: 'mdi-calendar',
                    format: FORM_DATE_FORMAT,
                    placeholder: FORM_DATE_FORMAT,
                    label: this.$t('road-maintenance/excavation-service/tabs/request/columns.request_date'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !validateDateTimeFormat(value, FORM_DATE_FORMAT)) {
                        updateValidationErrors({
                          created_at: this.$t('base.invalid_date').replace(':format', FORM_DATE_FORMAT)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: true,
                    doubleClickEdit: false
                  },
                  {
                    key: 'applicant_name',
                    row: 2,
                    column: 1,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/prp.request_applicant'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'request_street_name',
                    row: 2,
                    column: 2,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.request_address_street'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'request_house_number',
                    row: 2,
                    column: 3,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.request_address_house_number'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'work_type',
                    type: 'textarea',
                    row: 3,
                    column: 1,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.type_of_works_or_actions'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'regulation_setter',
                    row: 3,
                    column: 2,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/codebook_type.regulation_setter'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_name',
                    row: 3,
                    column: 3,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.contractor_name'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_responsible',
                    row: 4,
                    column: 1,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.contractor_responsible_person'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_phone',
                    row: 4,
                    column: 2,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.contractor_phone'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_email',
                    row: 4,
                    column: 3,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/aggregator.contractor_email'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    type: 'plain-text',
                    text: this.$t('road-maintenance/aggregator.consent_valid'),
                    row: 5,
                    column: 1,
                    colSpan: 2,
                    class: 'time-period-text',
                    colClass: 'time-period-column',
                    visible: true
                  },
                  {
                    type: 'plain-text',
                    text: this.$t('road-maintenance/aggregator.requested_date_from_request'),
                    row: 5,
                    column: 2,
                    colSpan: 2,
                    class: 'time-period-text',
                    colClass: 'time-period-column',
                    visible: true
                  },
                  {
                    key: 'valid_from',
                    type: 'datetime-picker',
                    row: 6,
                    column: 1,
                    colSpan: 1.5,
                    appendIcon: 'mdi-calendar',
                    format: DISPLAY_DATETIME_FORMAT,
                    placeholder: DISPLAY_DATETIME_FORMAT,
                    label: this.$t('road-maintenance/aggregator.date_from'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !validateDateTimeFormat(value, DISPLAY_DATETIME_FORMAT)) {
                        updateValidationErrors({
                          valid_from: this.$t('base.invalid_date').replace(':format', DISPLAY_DATETIME_FORMAT)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: true,
                    doubleClickEdit: false
                  },
                  {
                    key: 'valid_to',
                    type: 'datetime-picker',
                    row: 6,
                    column: 2,
                    colSpan: 1.5,
                    appendIcon: 'mdi-calendar',
                    format: DISPLAY_DATETIME_FORMAT,
                    placeholder: DISPLAY_DATETIME_FORMAT,
                    label: this.$t('road-maintenance/aggregator.date_to'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !validateDateTimeFormat(value, DISPLAY_DATETIME_FORMAT)) {
                        updateValidationErrors({
                          valid_to: this.$t('base.invalid_date').replace(':format', DISPLAY_DATETIME_FORMAT)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: true,
                    doubleClickEdit: false
                  },
                  {
                    key: 'date_from',
                    row: 6,
                    column: 3,
                    colSpan: 1.5,
                    appendIcon: 'mdi-calendar',
                    label: this.$t('road-maintenance/aggregator.date_from'),
                    onInput: (value, event) => {
                      const { updateData } = event || {}
                      if (value && typeof updateData === 'function') {
                        updateData({
                          valid_from: value
                        })
                      }
                    },
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'date_to',
                    row: 6,
                    column: 4,
                    colSpan: 1.5,
                    appendIcon: 'mdi-calendar',
                    label: this.$t('road-maintenance/aggregator.date_to'),
                    onInput: (value, event) => {
                      const { updateData } = event || {}
                      if (value && typeof updateData === 'function') {
                        updateData({
                          valid_to: value
                        })
                      }
                    },
                    creatable: false,
                    editable: false,
                    visible: true
                  }
                ]
              },
              {
                component: this.accountingFieldsRowComponent
              },
              {
                type: 'fields',
                columnsCount: 3,
                fields: [
                  {
                    key: 'restoration_responsibility',
                    label: this.$t('road-maintenance/aggregator.restoring_costs'),
                    type: 'autocomplete',
                    items: [
                      {
                        id: this.$t('road-maintenance/dropdown-types.contractor'),
                        name: this.$t('road-maintenance/dropdown-types.contractor')
                      },
                      {
                        id: this.$t('road-maintenance.zagreb-roads'),
                        name: this.$t('road-maintenance.zagreb-roads')
                      }
                    ],
                    row: 1,
                    column: 1,
                    colSpan: 1,
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: true,
                    doubleClickEdit: false
                  },
                  {
                    key: 'damage_responsibility',
                    label: this.$t('road-maintenance/aggregator.all_damages_incurring'),
                    type: 'autocomplete',
                    items: [
                      {
                        id: this.$t('road-maintenance/dropdown-types.contractor'),
                        name: this.$t('road-maintenance/dropdown-types.contractor')
                      },
                      {
                        id: this.$t('road-maintenance.zagreb-roads'),
                        name: this.$t('road-maintenance.zagreb-roads')
                      }
                    ],
                    row: 1,
                    column: 2,
                    colSpan: 1,
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: true,
                    doubleClickEdit: false
                  },
                  {
                    key: 'additional_costs_responsibility',
                    label: this.$t('road-maintenance/aggregator.costs_obligated'),
                    type: 'autocomplete',
                    items: [
                      {
                        id: this.$t('road-maintenance/dropdown-types.contractor'),
                        name: this.$t('road-maintenance/dropdown-types.contractor')
                      },
                      {
                        id: this.$t('road-maintenance.zagreb-roads'),
                        name: this.$t('road-maintenance.zagreb-roads')
                      }
                    ],
                    row: 1,
                    column: 3,
                    colSpan: 1,
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: true,
                    doubleClickEdit: false
                  }
                ]
              },
              {
                component: this.conditionFieldsRowComponent
              },
              {
                component: this.rowBlocksDataComponent,
                mainClasses: 'aggregator-approval-blocks-main',
                titleClasses: 'aggregator-approval-internal-title',
                wrapperClasses: 'aggregator-approval-card-text',
                title: this.$t('road-maintenance/aggregator.attachments'),
                cardTitle: 'aggregator-approval-card-title',
                visible: this.approvalDocumentsVisible,
                columnsConfig: this.approvalAttachmentsColumnsConfig,
                onRemoveBlock: (event) => {
                  const { key } = event?.block || {}
                  const { updateData, formData } = event || {}

                  if (key && typeof updateData === 'function') {
                    const { files } = formData || {}
                    // console.log(formData, key, files)
                    updateData({
                      files: files?.filter(file => file.key !== key)
                    })
                  }
                },
                buttons: [
                  {
                    label: this.$t('road-maintenance/aggregator.upload_attachment'),
                    color: '#65AD45',
                    prependIcon: 'mdi-plus',
                    prependIconStyle: { color: '#FFFFFF !important' },
                    style: { color: '#FFFFFF !important' },
                    click: (event) => this.onApprovalUploadAttachment(event)
                  }
                ],
                dataKey: 'documents'
              }
            ]
          },
          // Document history
          {
            component: this.rowBlocksDataComponent,
            mainClasses: 'aggregator-requests-blocks-main',
            wrapperClasses: 'aggregator-requests-subject-wrapper',
            title: this.$t('road-maintenance/aggregator.log_history'),
            visible: this.approvalHistoryVisible,
            externalData: this.logsData,
            dataKey: DOCUMENTS_HISTORY_DATA_KEY,
            showLoader: this.logsShowLoader,
            columnsConfig: [
              {
                key: 'created_at',
                title: this.$t('road-maintenance/aggregator.application_submission_date') + ':',
                valueClass: 'history-block-value',
                customValue: (data) => {
                  return data?.created_at ? formatIsoDateCustom(data.created_at, FORM_DATETIME_FORMAT, DISPLAY_DATETIME_FORMAT) : ''
                }
              },
              {
                key: 'status',
                title: this.$t('road-maintenance/aggregator.request_type') + ':',
                valueClass: 'history-block-value',
                customValue: (data) => {
                  return data?.status ? this.$t(data.status) : '-'
                }
              },
              {
                key: 'user',
                valueClass: 'history-block-value',
                title: this.$t('road-maintenance/aggregator.request_by') + ':'
              },
              {
                key: 'document',
                title: this.$t('road-maintenance/aggregator.document') + ':',
                valueClass: 'history-block-value',
                prependImage: {
                  src: '/img/icons/pdf-icon.svg',
                  style: {
                    marginRight: '4%'
                  }
                },
                contentWrapperStyle: {
                  display: 'flex',
                  alignItems: 'center'
                },
                customValue: (data) => {
                  return data?.document?.name ? data.document.name : '-'
                },
                click: async (event) => await this.downloadDocument(event)
              }
            ]
          },
          {
            component: this.documentPreviewComponent,
            visible: this.approvalShowDocumentPreview,
            content: this.approvalDocument,
            showLoader: this.approvalShowLoader
          }
        ]
      }
    }
  },

  methods: {
    async onApprovalGenerateDocument (event, showLast = false) {
      try {
        const { formData } = event || {}

        if (formData) {
          // Create formData object for sending
          const dataToSend = new FormData()
          dataToSend.append('valid_from', formData?.valid_from ? formatIsoDateCustom(formData.valid_from, DISPLAY_DATETIME_FORMAT, DATE_FORMAT_FROM_REQUEST) : '')
          dataToSend.append('valid_to', formData?.valid_to ? formatIsoDateCustom(formData.valid_to, DISPLAY_DATETIME_FORMAT, DATE_FORMAT_FROM_REQUEST) : '')
          dataToSend.append('created_at', formData?.created_at ? formatIsoDateCustom(formData.created_at, FORM_DATE_FORMAT, DATE_FORMAT_TO_SEND) : '')
          dataToSend.append('restoration_responsibility', formData?.restoration_responsibility ?? '')
          dataToSend.append('damage_responsibility', formData?.damage_responsibility ?? '')
          dataToSend.append('additional_costs_responsibility', formData?.additional_costs_responsibility ?? '')
          dataToSend.append('additional_terms', formData?.additional_terms?.id ?? formData?.additional_terms ?? '')
          dataToSend.append('payment_type', formData?.payment_type?.id ?? formData?.payment_type ?? '')
          dataToSend.append('additional_terms_note', formData?.additional_terms_note ?? '')
          dataToSend.append('erp_reference_number', formData?.erp_reference_number ?? '')
          dataToSend.append('erp_document_status', formData?.erp_document_status ?? '')
          dataToSend.append('show_last_version', showLast)

          // Get document html
          const { data } = await api('road-maintenance').post(`traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}`, dataToSend)

          this.approvalDocument = data
          this.$set(this.chooseSolutionTypeDialogConfig, 'showDialog', false)
          this.approvalShowDocumentPreview = true
          this.approvalEditState = true
          this.approvalShowEditButtons = true
          this.approvalDataForGenerationVisible = false
          this.approvalTitle = this.$t('road-maintenance/aggregator.approval_document_overview')
        }
      }
      catch (exception) {
        const { errors } = exception?.response?.data || {}
        this.approvalShowLoader = false
        this.approvalValidationErrors = errors && isObject(errors) ? errors : {}
        console.error(exception)
      }
    },

    onApprovalCancelTab () {
      this.$set(this.informationDialogConfig, 'title', this.$t('base.warning'))
      this.$set(this.informationDialogConfig, 'positiveButtonText', this.$t('base.confirm'))
      this.$set(this.informationDialogConfig, 'cancelButtonText', this.$t('base.cancel'))
      this.$set(this.informationDialogConfig, 'message', this.$t('fleet/vehicle.data_not_saved_message'))
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', () => this.approvalCancel())
      this.$set(this.informationDialogConfig, 'showDialog', true)
    },

    approvalCancel () {
      this.approvalEditState = false
      this.$set(this.informationDialogConfig, 'showDialog', false)
    },

    onApprovalEditUpdate (event) {
      const { edit } = event || {}
      this.approvalHistoryVisible = !edit

      if (!edit) {
        this.approvalShowDocumentPreview = false
        this.approvalEditState = false
        this.approvalShowEditButtons = false
        this.approvalDataForGenerationVisible = true
        this.approvalDocument = null
        this.approvalTitle = this.approvalBaseTitle
      }
    },

    async onApprovalDataFetched (event, afterSave = false) {
      const { data: apiData, updateData } = event || {}
      const { formDataForSend } = event || {}

      const data = apiData?.data || formDataForSend || {}

      if (typeof updateData === 'function' && data && isObject(data)) {
        updateData({
          // Update contractor
          contractor_name: data?.contractor?.name ?? null,
          contractor_responsible: data?.contractor?.responsible_person ?? null,
          contractor_phone: data?.contractor?.phone ?? null,
          contractor_email: data?.contractor?.email ?? null,

          // Update applicant
          applicant_name: (data?.applicant?.first_name ?? '') + ' ' + (data?.applicant?.last_name ?? ''),

          // Update data from request
          request_street_name: data?.geolocation?.address_street ?? null,
          request_house_number: data?.geolocation?.address_number ?? null,

          // Regulation setter
          regulation_setter: data?.regulation_setter?.description ? this.$t(data.regulation_setter.description) : null,

          // date from and to
          date_from: data?.date_from && data?.time_from ? data?.date_from + ' ' + data?.time_from : null,
          date_to: data?.date_to && data?.time_to ? data?.date_to + ' ' + data?.time_to : null
        })

        // Modify a created_at date to be in format: FORM_DATE_FORMAT only if it is not already in FORM_DATE_FORMAT
        if (data && data.created_at && !checkDateFormat(data.created_at, FORM_DATE_FORMAT)) {
          updateData({
            created_at: data?.created_at ? formatIsoDateCustom(data.created_at, DATE_FORMAT_FROM_REQUEST, FORM_DATE_FORMAT) : currentDatetime(FORM_DATE_FORMAT)
          })
        }
      }

      if (!this.approvalDocumentHistoryLoaded || afterSave) {
        this.approvalDocumentHistoryLoaded = true
        const { id: suggestedStatusId, description } = data?.suggested_status || {}

        // Fetch documents logs
        await this.fetchHistoryDocuments(DOCUMENT_TYPE, DOCUMENTS_HISTORY_DATA_KEY)

        // Open change status popup after documents save only if we have suggested status provided
        if (suggestedStatusId) {
          this.$set(this.afterDocumentStatusChangeActions, 'statusId', suggestedStatusId)
          const message = this.$t('road-maintenance/aggregator.status_change_message')
          this.$set(this.informationDialogConfig, 'showDialog', afterSave)
          this.$set(this.informationDialogConfig, 'message', '<div> ' + message + '<span style="font-style: italic; color: green;">' + ' ' + this.$t(description) + '</span>?</div>')
        }
      }
    },

    onApprovalModifyData (event) {
      const { originalData } = event || {}

      if (originalData) {
        originalData.created_at = originalData?.created_at ? formatIsoDateCustom(originalData.created_at, FORM_DATE_FORMAT, DATE_FORMAT_TO_SEND) : null
        originalData.valid_from = originalData?.valid_from ? formatIsoDateCustom(originalData.valid_from, DISPLAY_DATETIME_FORMAT, DATE_FORMAT_FROM_REQUEST) : null
        originalData.valid_to = originalData?.valid_to ? formatIsoDateCustom(originalData.valid_to, DISPLAY_DATETIME_FORMAT, DATE_FORMAT_FROM_REQUEST) : null
        if (originalData?.files?.length) {
          originalData.documents = originalData.files?.map(file => file?.file || null)
          delete originalData.files
        }
        else {
          delete originalData.documents
          delete originalData.files
        }
      }

      return originalData
    },

    onApprovalUploadAttachment (event) {
      this.$refs.file_input.value = ''
      this.$refs.file_input.click()
      this.approvalUploadAttachmentEvent = event
    },

    onDocumentInput (event) {
      try {
        const uploadedFiles = Array.from(event?.target?.files || [])
        const { updateData, formData } = this.approvalUploadAttachmentEvent || {}
        const existingObject = formData?.documents?.[0] || {}

        const allFiles = [
          ...Object.values(existingObject),
          ...uploadedFiles
        ]

        if (!allFiles.length || typeof updateData !== 'function') {
          return
        }

        const newObject = {}
        this.approvalAttachmentsColumnsConfig = allFiles.map((file, index) => {
          const key = `document_${index}`
          newObject[key] = file

          const returnObject = {
            key,
            deletable: true,
            title: this.$t('road-maintenance/aggregator.document') + ':',
            customValue: () => {
              return file?.name || file?.file_name || '-'
            }
          }

          // download is possible only if attachment is saved on Centrix
          if (file?.file_id) {
            returnObject.click = () => this.downloadApprovalAttachment(file)
          }
          return returnObject
        })

        const files = Array.isArray(allFiles)
          ? allFiles
            .filter(file => file && file.name)
            .map((file, index) => ({
              file,
              key: `document_${index}`
            }))
          : []

        updateData({
          documents: [newObject]
        })

        if (files?.length) {
          updateData({
            files
          })
        }
      }
      catch (exception) {
        store.dispatch('base/notifications/push', this.$t('base.server_error'))
        console.error(exception)
      }
    },

    async approvalMenuItemClick (event) {
      const { menuIndex } = event || {}
      await this.onApprovalGenerateDocument(event, menuIndex === 1)
    },

    async downloadApprovalAttachment (attachment) {
      let objectURL = null
      try {
        const { file_id: fileId } = attachment || {}
        if (fileId) {
          const url = new URL(`${window.location.origin}/api/road-maintenance/traffic-regulation/${this.$route.params.id}/attachment/${fileId}`)

          const response = await fetch(url.toString(), {
            method: 'POST'
          })

          if (!response.ok) {
            console.error(`Failed to show PDF: HTTP ${response.status} - ${response.statusText}`)
            return
          }

          const contentType = response.headers.get('Content-type')
          const contentDisposition = response.headers.get('Content-Disposition')
          const arrayBuffer = await response.arrayBuffer()

          // Create a Blob from the response data
          const blob = new Blob([new Uint8Array(arrayBuffer)], {
            type: contentType
          })

          // Generate object URL for downloading
          objectURL = URL.createObjectURL(blob)
          const filename = contentDisposition?.split('filename=')[1] ?? 'File'

          // Trigger download
          const link = document.createElement('a')
          link.href = objectURL
          link.download = filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      }
      catch (exception) {
        console.error(exception.message || exception)
      }
      finally {
        if (objectURL) {
          URL.revokeObjectURL(objectURL)
        }
      }
    }
  }
}
